import {
  booking_status,
  payment_status,
} from "@/modules/checkin-facility/utils/mockup";
import _, { times } from "lodash";
import moment from "moment-timezone";
import t from "typy";
import { mapActions, mapGetters, mapState } from "vuex";
import LanguageButtonComponent from "../../dashboard/components/language-button.component";
import CheckoutDetails from "../components/checkout/CheckoutDetails.vue";
import GatewayPayment from "../components/gatewayPayment.vue";
import LeaveWarningModal from "../components/LeaveWarningModal.vue";
import CheckoutGuideComponent from "./../../../components/checkout-guide.component";
import TermsComponent from "./../../../components/terms.component";
import GuideComponent from "./../../../components/guide.component.vue";
import CheckoutFormByPhone from "./checkout_by_phone.vue";
import CheckoutKioskHub from "../kiosk/checkout_kiosk_hub_component.vue";
import KioskIdleWarningModal from "../kiosk/KioskIdleWarningModal.vue";
import kioskValidationMixin from "../kiosk/kiosk_validation_mixin";
import { isKioskRoute } from "../kiosk/kiosk_config";
import {
  applyKioskLanguage,
  rememberKioskLanguage,
} from "../kiosk/kiosk_language";
import loader from "@/services/loader";
import { defaultsDeep } from "lodash";
import EmailField from "../components/EmailField.vue";
import { eventBus } from "@/main";
import CheckoutInformation from "../components/checkout/index.vue";

// Modals that hand the screen to a payment gateway's own iframe (Digital
// Garage, fincode). Both card components use the first id; the second is the
// "processing" window the gateway puts up between sending the payment request
// and getting its result — the one stretch of the flow where the guest cannot
// act at all, and so must never be counted as an absent one.
const PAYMENT_MODAL_IDS = ["payment-modal", "processing-modal"];

export default {
  components: {
    TermsComponent,
    CheckoutGuideComponent,
    LanguageButtonComponent,
    CheckoutDetails,
    GatewayPayment,
    LeaveWarningModal,
    GuideComponent,
    CheckoutFormByPhone,
    CheckoutKioskHub,
    KioskIdleWarningModal,
    EmailField,
    CheckoutInformation
  },
  mixins: [kioskValidationMixin],
  data() {
    return {
      hideButton: false,
      typy: t,
      submit_btn: "",
      error_msg: "",
      success_msg: "",
      confirm: false,
      facility: {
        name: "...",
      },
      user: {
        email: "",
        key: "",
      },
      form_language: "",
      enabled_languages: [],
      is_custom: false,
      custom_text_setting: {},
      step: "prematch",
      paid_statuses: [
        payment_status.pay_at_reception,
        payment_status.paid_authorized,
        payment_status.paid_captured,
        payment_status.paid,
      ],
      paid: false,
      currency: "",
      invalid_code_error: [],
      is_loading: true,
      checkout_is_loading: false,
      navigationConfirmed: false,
      formIsDirty: false,
      isCheckoutNotAvailable: false,
      errorCheckoutTimingNotAvailable: "",
      reservation_no: "",
      mealDetails: {},
      checkout_date: "",
      is_searching: false,
      error_messages: [],
      facilityInfo: [],
      email_receipt: null,
      dual_payment_mode: false,

      // Reported by the check-out hub: a guest is part-way through a search
      // there (panel open, camera up, lookup in flight).
      kiosk_hub_busy: false
    };
  },
  // Same as the check-in form: ahead of the first render, because a fresh
  // document starts at `en` and the boot below fetches its custom text in
  // whatever locale is current.
  beforeCreate() {
    if (isKioskRoute(this.$route)) applyKioskLanguage(this.$i18n);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    this.$root.$off("bv::modal::show", this.kioskOnModalShow);
    this.$root.$off("bv::modal::hidden", this.kioskOnModalHidden);
  },

  async mounted() {
    // Guest Hub: same reason as the check-in form — the boot below is a run of
    // sequential requests, and a per-request overlay blinks off between each
    // pair. Held for the whole boot, kiosk only, released in a `finally`.
    if (this.isKioskCheckout) loader.hold();

    try {
      eventBus.$on("updateShowPayment", (showPayment, dualPaymentMode) => {
        this.isPaid(showPayment, dualPaymentMode);
      });
      // window.addEventListener('beforeunload', this.handleBeforeUnload);
      // console.log(this.$route)

      this.facility = await this.$lionheart
        .post("/facility/list", { slug_name: this.$route.params.slug_name })
        .then((r) => this.catcher(r.data, "data[0].id", (r) => r.data[0]))
        .catch((e) => this.catcher(e));
      if (this.facility.error.has_error) return this.$router.push("/404");

      const el = await this.$lionheart.post("/facility-setting/list", {
        facility_id: this.facility.id,
        name: "language",
        paginate: false,
      });

      if (el.data.total != 0)
        el.data.data[0].value.forEach((value, key) => {
          this.enabled_languages.push(value.code);
        });
      else this.enabled_languages.push("en", "ja");

      const pms_settings = await this.$lionheart.post("/facility-setting/list", {
        facility_id: this.facility.id,
        name: "pms_settings",
        paginate: false,
      });

      if (pms_settings.data.total != 0)
        this.facility.pms_code = pms_settings.data.data[0].value.pms_code;
      else return this.$router.push("/404");

      // Idle auto-reset needs the Guest Hub timings. Only the kiosk has them;
      // the public check-out form is left exactly as it was.
      if (this.isKioskCheckout) {
        await this.kioskLoadConfig(this.facility.id);
        this.kioskWatchCheckoutWindow();
        this.kioskWatchPaymentScreen();
        // Already true on arrival, so its watcher never sees it change.
        if (this.kioskSessionInProgress) this.kioskStartIdleWatch();
      }

      this.checkCheckouTimingSettings();

      this.is_custom = this.facility.facility_setting.length > 0 ? true : false;

      this.loadCustomTextSetting(this.$i18n.locale, this.facility.id);

      // Set checkout date for phone form
      this.checkout_date = moment().format("YYYY/MM/DD");

      this.is_loading = false;

      // Auto confirm for QR checkout
      if (this.$route.query.e && this.$route.query.k) {
        this.is_loading = true;
        this.user.email = this.$route.query.e;
        this.user.key = this.$route.query.k;

        // Ensure facility and pms_code are ready before submitting
        setTimeout(() => this.submit("confirm"), 500);
      }
    } finally {
      if (this.isKioskCheckout) loader.release();
    }
  },
  computed: {
    ...mapGetters("checkin-site", ["booking"]),
    ...mapState("guest", ["isCheckoutFormEmpty"]),
      // The Guest Hub check-out screen reaches this route as
      // `checkout-core?kiosk=true`, so it carries no meta of its own.
      isKioskCheckout() {
         return isKioskRoute(this.$route);
      },
      // The kiosk hub is the screen on show: the method picker the "Start
      // Check-out" button opens, before the guest has been matched to a
      // booking. It owns the form title and the account's message while it
      // is up.
      kioskHubOnScreen() {
         return this.isKioskCheckout
            && this.confirm === false
            && this.step === "prematch";
      },
      // The check-out hub renders while `confirm === false && step === 'prematch'`,
      // so anything past that is the form the guest is actually filling in.
      onKioskCheckoutForm() {
         return this.isKioskCheckout
            && !(this.confirm === false && this.step === "prematch");
      },
      // The check-out is done and this is the "check-out complete" screen
      // (completeCheckout leaves confirm null on the prematch step). Nothing
      // is being filled in any more, so it is governed by the plain 60-second
      // return instead of the warning countdown.
      kioskOnCheckoutComplete() {
         return this.isKioskCheckout
            && this.confirm === null
            && this.step === "prematch";
      },
      // A guest is on the check-out flow: the hub the "Start Check-out" button
      // opened, or the form behind it (details, payment, confirmation). The
      // home screen is the landing page now, so an abandoned hub is a started
      // session like the check-in hub, and the idle watch runs on this.
      kioskSessionInProgress() {
         return this.isKioskCheckout && !this.kioskOnCheckoutComplete;
      },
      // A guest is in the middle of something: filling the check-out form or
      // part-way through a search on the hub. This is what the end of the
      // operating window waits for, so a check-out already under way is never
      // cut short (see kioskCloseIfUnattended). An untouched hub can close.
      kioskGuestEngaged() {
         return this.kioskSessionInProgress
            && (this.onKioskCheckoutForm || this.kiosk_hub_busy);
      },
      hasInvalidEmail() {
         // Both the by-phone route and the kiosk hub find the booking by
         // search, so the address the receipt goes to is whatever the
         // reservation carries — and that is sometimes a dummy.
         if(this.$route.meta?.checkout_phone == true || this.isKioskCheckout) {
            const booking = this.booking;
            console.log('booking', booking)
            // should this check on the facility visitor log email also?
            // if there is an email to facility visitor log hide this 
            if(booking && booking.data && booking.data.length > 0) {
               const dummyEmailPattern = /(.+@dummy\.com.*)|(.+@dummy\.co\.jp)|(dummy@nta.*)/i;
               const isDummy = booking.data[0].facility_visitor_log?.visitor?.user?.email;
               const visitorEmail = dummyEmailPattern.test(isDummy) ? "" : booking.data[0].facility_visitor_log?.visitor?.user?.email || "--" || "";
               const email = visitorEmail || booking.data[0].parsed.email_address || null || "--";
               return email === null || email === "" || email === "--";
            }
         }
      },
  },

  methods: {
    ...mapActions("checkin-site", ["setBooking"]),
    ...mapActions("guest", ["setCheckoutFormEmpty"]),

    async fetchMealDetails(rsvNo, facilityId, pass) {
      const payload = {
        reservation_no: rsvNo,
        facility_id: facilityId,
        pass: pass,
      };

      try {
        const response = await this.$lionheart.get("/meal-reservation/meal", {
          params: payload,
        });
        this.mealDetails = response.data;
      } catch (error) {
        console.error("Error fetching meal data:", error);
      }
    },

    async fetchFacilityInMrs(facilityId) {
      try {
        const response = await this.$mrsApi.get(`/facility?checkin_facility_id=${facilityId}`);
        this.facilityInfo = response.data;
        console.log('facilityInfo', this.facilityInfo)
      } catch (error) {
        console.error('Error fetching facility info:', error);
      }
    },

    confirmLeave() {
      this.redirectBack();
      this.$bvModal.hide("leave-warning-modal");
    },

    stayOnPage() {
      this.$bvModal.hide("leave-warning-modal");
    },

    markChanges() {
      this.formIsDirty = true;
    },

    handleBeforeUnload(event) {
      if (this.formIsDirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    },

    submit(action) {
      this.submit_btn = action;
      const resultValidation = this.validateEmailCheckoutReceipt();
      console.log("resultValidation:", resultValidation);
      if (!resultValidation) {
        return;
      }

      if (this.$route.meta?.checkout_phone == true && this.step === 'prematch') {
        this.$refs.checkoutByPhone.handlePhoneSearch();
      } else {
        setTimeout(() => this.$refs.checkoutForm.click(), 500);
      }
    },

    // Guest Hub: same span and same reason as the check-in form — everything
    // below validates or pays, and while it runs the guest is told not to go
    // back or refresh by the overlay this raises. Off the kiosk nothing
    // changes.
    async submitForm(e) {
      if (!this.isKioskCheckout) return this.runSubmitForm(e);

      loader.beginProcessing();
      try {
        return await this.runSubmitForm(e);
      } finally {
        loader.endProcessing();
      }
    },

    async runSubmitForm(e) {
      e.preventDefault();
      this.error_msg = "";
      this.success_msg = "";
      this.formIsDirty = false;

      if (this.submit_btn == "confirm") {
        if (this.step == "prematch") return this.confirmation();
        if (this.step == "match") return this.paymentConfirmation();
      }
    },

    confirmCloseTab() {
      this.$bvModal.hide("modal-close-confirmation");

      // Set formIsDirty to false to prevent browser's "Leave site?" dialog
      this.formIsDirty = false;

      window.close();

      setTimeout(() => {
        if (!window.closed) {
          this.$bvToast.toast(
            this.$t("visitor-checkout-form.please_close_tab_manually"),
            {
              variant: "info",
              solid: true,
              autoHideDelay: 5000,
            }
          );
        }
      }, 100);
    },

    async confirmation() {
      const user = this.user;
      const data = {
        facility_id: this.facility.id,
        pass: user.key,
        email: user.email,
        mode: "pre-checkin",
        intentMode: "checkout",
      };

      data.mode = "pre-checkin";
      let response = await this.$lionheart
        .post("/v2/facility-booking/list", data)
        .catch((e) => this.catcher(e));

      let booking = response.data;

      this.reservation_no = booking.data[0]?.pms_reservation_no ?? "";

      if (booking.data.length == 0) {
        data.mode = "by-pass-email-with-formlog";
        response = await this.$lionheart
          .post("/v2/facility-booking/list", data)
          .catch((e) => this.catcher(e));

        booking = response.data;

        // SET THE EMAIL ADDRESS RETRIEVED INSIDE WHEN DATE MODE IS BY-PASS-EMAIL-WITH-FORMLOG
        // IF THE RESULT IS NOT FOUND, THEN SEND A REQUEST TO SEARCH TO THE METADATA
        // CHANGE THE MODE TO BY-EMAIL-PASS

        if (booking.data.length > 0) {
          const log = booking.data[0]?.facility_visitor_log;

          if (log) {
            const visitor = log?.visitor;
            const user = visitor?.user;
            this.user.email = user?.email ?? "";
          } else {
            this.user.email = "";
          }
        }
      }

      if (booking.data.length == 0) {
        this.is_loading = false;
        this.invalid_code_error.push(
          "We could not find a reservation with this email and pass. Please try again"
        );

        setTimeout(() => {
          this.invalid_code_error.shift();
        }, 5000);
        return;
      }

      if (booking.data[0].payment_metadata) {
        this.currency = booking.data[0].payment_metadata.hasOwnProperty(
          "currency"
        )
          ? booking.data[0].payment_metadata.currency
          : "JPY";
      }

      if (!booking.success) return this.errorDisplay(booking.message);
      if (
        booking.data.length == 0 ||
        booking.data[0].booking_status_id != booking_status.checked_in
      )
        return this.errorDisplay(
          this.$t("visit-information-form.no_reservation_msg")
        );

      this.setBooking(booking);
      this.is_loading = false;

      if (booking.data[0].payment_metadata == null) return this.checkout(false);

      // // await this.resolveCheckoutStep();
      if (this.paid) {
        this.confirm = true;
        this.step = "confirmation";
        return;
      }

      this.step = "match";
    },

    // `credentials` is passed by search forms that already know the email /
    // pass pair they matched on (the kiosk hub). Without it they are read off
    // the booking, as the by-phone form has always done.
    async handleBookingFound(booking, credentials = null) {
      try {
        if (booking.data.length == 0 || booking.data[0].booking_status_id != booking_status.checked_in) {
          this.setBooking({});
          return this.errorDisplay(
            this.$t("visit-information-form.no_reservation_msg")
          );
        }

        if (booking.data[0].payment_metadata) {
          this.currency = booking.data[0].payment_metadata.hasOwnProperty("currency")
            ? booking.data[0].payment_metadata.currency
            : "JPY";
        }

        this.setBooking(booking);

        // Set before the no-payment shortcut below: checkout() posts this pair,
        // so it has to be in place whichever branch runs.
        this.user.email = credentials?.email
          ?? booking.data[0].facility_visitor_log?.visitor?.user?.email
          ?? "";
        this.user.key = credentials?.pass ?? booking.data[0].pass ?? "";

        if (booking.data[0].payment_metadata == null)
          return this.checkout(false);

        this.step = "match";
      } catch (error) {
        console.log("Error in handleBookingFound:", error);
        setTimeout(() => {
          this.invalid_code_error = [];
        }, 5000);
      }
    },

    async paymentConfirmation() {
      const status = await this.$refs.gatewayPayment
        .doCall()
        .createPaymentMethod();
      if (status) {
        this.confirm = true;
        this.step = "confirmation";
        window.scrollTo(0, 10);
      }
    },

    async checkout(doPayment = true) {
      const resultValidation = this.validateEmailCheckoutReceipt();
      if (!resultValidation) {
        return;
      }
      const data = {
        key: this.user.key,
        email: this.user.email,
        pms_code: this.facility.pms_code,
        timezone: moment.tz.guess(),
        facility_id: this.facility.id,
        email_receipt: this.email_receipt
        // mode: "log_id"
      };

      if (doPayment) {
        const payment = await this.$refs.gatewayPayment
          .doCall()
          .makePayment(true);
        if (!payment) {
          this.confirm = false;
          setTimeout(() => this.$refs.gatewayPayment.doCall().reInit(), 300);
          return;
        }
      }

      // for walkin no reservation no
      if(this.reservation_no) {
        this.fetchMealDetails(
          this.reservation_no,
          this.facility.id,
          this.user.key
        );

        this.fetchFacilityInMrs(this.facility.id);
      }

      const checkout = await this.$lionheart
        .post("/facility-key/g/checkout", data)
        .then((r) => this.catcher(r.data, "data.id", (r) => r.data))
        .catch((e) => this.catcher(e));

      if (checkout.error.has_error) {
        this.confirm = false;
        return this.errorDisplay(checkout.error.msg);
      } else {
        if (this.mealDetails?.hasMealData && this.facilityInfo[0]?.status == "enabled" && this.facilityInfo.length > 0) {
          const payload = {
            reservation_no: this.reservation_no,
            status: "completed",
            pass: this.user.key,
            pms_code: this.facility.pms_code,
          };

          const mrsUpdate = await this.$mrsApi
            .patch(`/reservations/update-status`, payload)
            .then((r) => this.catcher(r.data, null, (r) => r))
            .catch((e) => this.catcher(e));

          if (mrsUpdate.error.has_error) {
            this.confirm = false;
            return this.errorDisplay(mrsUpdate.error.msg);
          } else {
            this.completeCheckout();
          }
        } else {
          this.completeCheckout();
        }
      }
    },

    completeCheckout() {
      this.user.key = "";
      this.user.email = "";
      this.confirm = null;
      this.step = "prematch";
      this.setBooking({});
    },

    async back() {
      const formEmpty = await this.isCheckoutFormEmpty;

      if (formEmpty === false) {
        this.$bvModal.show("leave-warning-modal");
      } else {
        this.redirectBack();
      }
    },

      // ---- Operating window (kiosk) ----------------------------------

      // The end of the check-out window is watched here, on the page that owns
      // the whole flow, and not on the hub it starts from: the hub is torn
      // down the moment a booking matches, while this component stays mounted
      // through the form and the payment — which is what lets a close wait for
      // the guest instead of interrupting them.
      //
      // Nothing to watch when the hub is already shut on arrival: the hub's own
      // entry check sends that guest to the home screen, and a window that was
      // never open has no closing to announce.
      kioskWatchCheckoutWindow() {
        if (!this.kioskCanStartCheckout()) return;
        this.kioskStartClock(() => this.kioskEvaluateCheckoutWindow());
      },

      kioskEvaluateCheckoutWindow() {
        if (this.kioskCanStartCheckout()) return;

        this.kiosk_close_pending = true;
        this.kioskCloseIfUnattended();
      },

      // Closing happens between guests, never through one. Anyone who started
      // a check-out before the window ended finishes it first — on the form
      // (details, payment, confirmation) or half-way through a search on the
      // hub. The close then lands when they are back on an untouched hub, or
      // when the idle auto-reset decides nobody is there (kioskAutoReset).
      kioskCloseIfUnattended() {
        if (!this.kiosk_close_pending) return;
        if (this.kioskGuestEngaged) return;

        this.kioskGoHome({ closing: true });
      },

      // ---- Payment screen (kiosk) ------------------------------------

      // The card form belongs to the payment gateway and runs in its own
      // iframe, so nothing the guest types while paying reaches this page —
      // to the idle timer, someone entering a card number is indistinguishable
      // from an abandoned tablet, and the kiosk used to reset mid-payment.
      // Bootstrap's modal events are the one signal that does cross, so they
      // are what starts and stops the countdown around the payment window.
      kioskWatchPaymentScreen() {
        this.$root.$on("bv::modal::show", this.kioskOnModalShow);
        this.$root.$on("bv::modal::hidden", this.kioskOnModalHidden);
      },

      kioskOnModalShow(bvEvent, modalId) {
        if (!PAYMENT_MODAL_IDS.includes(modalId)) return;
        this.kioskSuspendIdleWatch();
      },

      kioskOnModalHidden(bvEvent, modalId) {
        if (!PAYMENT_MODAL_IDS.includes(modalId)) return;
        this.kioskResumeIdleWatch();
      },

      resetMainCheckout() {
        this.user.email = "";
        this.user.key = "";
        this.error_msg = "";
        this.invalid_code_error = [];
      },

    redirectBack() {
      this.confirm = false;
      this.error_msg = "";
      this.invalid_code_error = [];

      if (this.$route.meta?.checkout_phone) {
        this.step = "prematch";
        this.user.email = "";
        this.user.key = "";
        this.setBooking({});

        // TODO : $refs.checkoutByPhone should be general custom child component  e.g. customCheckoutSearchForm > then loading checkoutByPhone or any other component. then resetForm will be an interface
        if (this.$refs.checkoutByPhone) {
          this.$refs.checkoutByPhone.resetForm();
        }

        return;
      }

      // Kiosk: Back lands on the method hub again, except mid-payment where it
      // still steps back to the payment form the way the default flow does.
      if (this.isKioskCheckout) {
        if (this.step == "confirmation" && !this.paid) {
          this.step = "match";
          this.confirm = true;
          setTimeout(() => this.$refs.gatewayPayment.doCall().reInit(), 300);
          return;
        }

        // The hub is v-if'd on this step, so it comes back as a fresh instance
        // with nothing of the previous search left in it (8. Privacy).
        this.step = "prematch";
        this.confirm = false;
        this.setBooking({});
        this.resetMainCheckout();
        return;
      }

      if (this.step == "match") {
        this.step = "prematch";
        this.confirm = false;
        this.setBooking({});
        this.resetMainCheckout();
        return;
      }

      if (this.step == "confirmation") {
        if (this.paid) {
          this.step = "prematch";
           this.confirm = false;
           this.setBooking({});
           this.resetMainCheckout();
        } else {
          this.step = "match";
          this.confirm = true;
          setTimeout(() => this.$refs.gatewayPayment.doCall().reInit(), 300);
        }
      }
    },

   isPaid(showPayment, dualFromChild) {
      console.log("[isPaid] Checking if paid with showPayment:", showPayment, "and dualFromChild:", dualFromChild);
      if (showPayment == 0) {
        this.step = "confirmation";
        return (this.paid = true);
      }
      
      const isPaid = this.paid_statuses.includes(
        this.booking.data[0].payment_status_id
      );
      
      if(dualFromChild == true) {
         this.step = "match";
         this.confirm = false;
         const net_charge = this.booking.data[0].parsed?.net_charge ?? 0;

         if(net_charge == 0 && isPaid) {
            this.step = "confirmation";
            this.confirm = true;
            return (this.paid = true);
         }
         return (this.paid = false);
      }
      return (this.paid = isPaid);
    },

    errorDisplay(d) {
      this.is_loading = false;
      this.error_msg = d;
      return;
    },

    successDisplay(d) {
      this.success_msg = d;
      return;
    },

    async reloadQuestion(lang) {
      this.form_language = lang;
      this.$i18n.locale = lang;
      if (this.isKioskCheckout) rememberKioskLanguage(lang);
      this.confirm = false;

      // Clear all error messages when language changes
      this.invalid_code_error = [];
      this.error_msg = "";

      this.loadCustomTextSetting(lang, this.facility.id);
    },

    async loadCustomTextSetting(lang, id) {
      let params;

      if (this.is_custom) {
        params = {
          name: `message-checkout-form-${lang}`,
          fallback: "message-checkout-form-en",
        };
      } else {
        params = {
          name: `message-checkout-form-${lang}`,
          fallback: "message-checkout-form-en",
        };
      }

      const response = await this.$lionheart.get("/custom-messages/" + id, {
        params: params,
      });
      this.custom_text_setting = response.data.data.value;
    },

    async checkCheckouTimingSettings() {
      const timezonSetting = await this.$lionheart.get("v2/facility-settings", {
        params: {
          facility_id: this.facility.id,
          name: "timezone",
          paginate: 0,
        },
      });
      const timezone =
        timezonSetting.data.data.length > 0
          ? timezonSetting.data.data[0].value
          : "Asia/Tokyo";
      const checkoutTimingSettings = await this.$lionheart.get(
        "v2/facility-settings",
        {
          params: {
            facility_id: this.facility.id,
            name: "checkout_timing_settings",
            paginate: 0,
          },
        }
      );
      const timing =
        checkoutTimingSettings.data.data.length > 0
          ? checkoutTimingSettings.data.data[0].value
          : { from: "00:00", to: "23:59" };

      const checkoutFromTimingSetting = timing.from;
      const checkoutToTimingSetting = timing.to;

      const facilityCurrenTimeZone = moment.tz(timezone);
      const facilityTimeOnly = moment(
        facilityCurrenTimeZone.format("HH:mm"),
        "HH:mm"
      );
      const fromCheckoutTiming =
        moment(checkoutFromTimingSetting, "HH:mm") ?? null;
      const toCheckoutTiming = moment(checkoutToTimingSetting, "HH:mm") ?? null;

      const isCheckoutTimingInRange = facilityTimeOnly.isBetween(
        fromCheckoutTiming,
        toCheckoutTiming,
        undefined,
        "[]"
      );
      const fromTime = fromCheckoutTiming.format("HH:mm");
      const toTime = toCheckoutTiming.format("HH:mm");
      const errorMessage = this.$t(
        "visitor-checkout-form.error_checkout_not_available_note",
        { fromTime: fromTime, toTime: toTime }
      );
      this.errorCheckoutTimingNotAvailable = errorMessage;
      this.isCheckoutNotAvailable = true;
      if (isCheckoutTimingInRange === true) {
        this.errorCheckoutTimingNotAvailable = "";
        this.isCheckoutNotAvailable = false;
      }
    },

    validateEmailCheckoutReceipt(){
      if (this.hasInvalidEmail && this.$refs.emailField) {
        const isEmailValid = this.$refs.emailField.validate();
        if (!isEmailValid) {
          return false;
        }
      }
      return true;
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.formIsDirty) {
      this.$once("confirm", () => {
        this.formIsDirty = false;
        next();
      });
      this.$once("cancel", () => {
        next(false);
      });
    } else {
      next();
    }
  },
  watch: {
    kioskSessionInProgress(active) {
      if (active) return this.kioskStartIdleWatch();
      this.kioskStopIdleWatch();
    },

    kioskGuestEngaged(engaged) {
      if (engaged) return;
      // The completed screen runs its own minute and then leaves; a closing
      // that was waiting on this guest rides out with it (kioskAutoReset), so
      // the last thing they see is their receipt, not the closing notice.
      if (this.kioskOnCheckoutComplete) return;
      // The guest is done and the hub is untouched again, so a closing that
      // was waiting on them can finally happen.
      this.kioskCloseIfUnattended();
    },

    // 7. Session Management: the completed screen carries no warning — it just
    // returns to the start of the flow a minute after the check-out finishes,
    // so the next guest never finds the last one's receipt on the tablet.
    kioskOnCheckoutComplete(done) {
      if (!this.isKioskCheckout) return;
      if (done) return this.kioskStartAutoReturn();
      this.kioskStopAutoReturn();
    },
    isCheckoutFormEmpty(val) {
      if (val === undefined) {
        this.setCheckoutFormEmpty(true);
      } else {
        this.setCheckoutFormEmpty(val);
      }
    },
  },
};
